'use client';

import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Check, X, AlertCircle } from 'lucide-react';
import type { Space, Seat, Reservation, SpaceTheme } from '../../types';
import { SPACES } from '../../data/mock-data';
import { SeatLayoutModal } from '../modals/SeatLayoutModal';

interface SpaceBookingTabsProps {
  selectedDate: Date;
  seats: Seat[];
  reservations: Reservation[];
  onReserveSeat: (spaceId: string, seat: Seat) => void;
  onConfirmSeat: (reservationId: string) => void;
  onCancelReservation: (reservationId: string) => void;
  currentUserId: string;
}

export function SpaceBookingTabs({
  selectedDate,
  seats,
  reservations,
  onReserveSeat,
  onConfirmSeat,
  onCancelReservation,
  currentUserId
}: SpaceBookingTabsProps) {
  const [layoutModal, setLayoutModal] = useState<{ isOpen: boolean; space: Space | null }>({
    isOpen: false,
    space: null
  });
  const [selectedSeat, setSelectedSeat] = useState<{ spaceId: string; seat: Seat } | null>(null);

  const formatDate = (date: Date) => {
    return `${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  const formatDateForDB = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 공간별 좌석 현황 계산
  const getSeatStats = (spaceId: string) => {
    const spaceSeats = seats.filter(s => s.space_id === spaceId);
    const available = spaceSeats.filter(s => s.status === 'available').length;
    const occupied = spaceSeats.filter(s => s.status === 'occupied').length;
    const reserved = spaceSeats.filter(s => s.status === 'reserved' || s.status === 'my-reserved').length;
    return { total: spaceSeats.length, available, occupied, reserved };
  };

  // 현재 사용자의 해당 날짜 예약 가져오기
  const getMyReservation = (spaceId: string): Reservation | undefined => {
    const dateString = formatDateForDB(selectedDate);
    return reservations.find(
      res => res.space_id === spaceId &&
             res.reservation_date === dateString &&
             res.user_id === currentUserId &&
             res.status !== 'cancelled'
    );
  };

  const getThemeClasses = (theme: SpaceTheme) => {
    switch (theme) {
      case 'purple':
        return {
          badge: 'bg-purple-500 hover:bg-purple-600 text-white',
          button: 'bg-purple-500 hover:bg-purple-600',
          lightBg: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-600',
          progress: 'bg-purple-500',
        };
      case 'orange':
        return {
          badge: 'bg-orange-500 hover:bg-orange-600 text-white',
          button: 'bg-orange-500 hover:bg-orange-600',
          lightBg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-600',
          progress: 'bg-orange-500',
        };
      case 'green':
        return {
          badge: 'bg-green-500 hover:bg-green-600 text-white',
          button: 'bg-green-500 hover:bg-green-600',
          lightBg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-600',
          progress: 'bg-green-500',
        };
    }
  };

  const handleSeatSelect = (space: Space, seat: Seat) => {
    setSelectedSeat({ spaceId: space.id, seat });
    setLayoutModal({ isOpen: false, space: null });
  };

  const handleReserve = () => {
    if (selectedSeat) {
      onReserveSeat(selectedSeat.spaceId, selectedSeat.seat);
      setSelectedSeat(null);
    }
  };

  // 스터디룸 그룹화 (A, B를 하나의 탭으로)
  const groupedSpaces = [
    SPACES.find(s => s.id === 'focus')!,
    {
      id: 'group',
      name: '스터디룸',
      capacity: 8,
      theme: 'orange' as SpaceTheme,
      description: '4인 그룹 스터디룸 (A, B)',
      subSpaces: SPACES.filter(s => s.id.startsWith('group-'))
    },
    SPACES.find(s => s.id === 'open')!,
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b">
        <h2 className="font-medium">{formatDate(selectedDate)} 좌석 현황</h2>
      </div>

      <Tabs defaultValue="focus" className="w-full">
        <div className="px-4 pt-4">
          <TabsList className="grid w-full grid-cols-3 h-10">
            <TabsTrigger value="focus" className="text-xs sm:text-sm">
              포커스룸
            </TabsTrigger>
            <TabsTrigger value="group" className="text-xs sm:text-sm">
              스터디룸
            </TabsTrigger>
            <TabsTrigger value="open" className="text-xs sm:text-sm">
              오픈석
            </TabsTrigger>
          </TabsList>
        </div>

        {/* 포커스룸 */}
        <TabsContent value="focus" className="p-4 mt-0">
          {renderSpaceContent(SPACES.find(s => s.id === 'focus')!)}
        </TabsContent>

        {/* 스터디룸 (A, B) */}
        <TabsContent value="group" className="p-4 mt-0">
          <div className="space-y-4">
            {SPACES.filter(s => s.id.startsWith('group-')).map(space => (
              <div key={space.id}>
                {renderSpaceContent(space, true)}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* 오픈석 */}
        <TabsContent value="open" className="p-4 mt-0">
          {renderSpaceContent(SPACES.find(s => s.id === 'open')!)}
        </TabsContent>
      </Tabs>

      {/* 배치도 모달 */}
      {layoutModal.space && (
        <SeatLayoutModal
          isOpen={layoutModal.isOpen}
          onClose={() => setLayoutModal({ isOpen: false, space: null })}
          space={layoutModal.space}
          seats={seats}
          onSelectSeat={(seat) => handleSeatSelect(layoutModal.space!, seat)}
          selectedSeatId={selectedSeat?.seat.id}
        />
      )}
    </div>
  );

  function renderSpaceContent(space: Space, compact = false) {
    const themeClasses = getThemeClasses(space.theme);
    const stats = getSeatStats(space.id);
    const myReservation = getMyReservation(space.id);
    const occupancyRate = Math.round((stats.occupied / stats.total) * 100);
    const spaceSeats = seats.filter(s => s.space_id === space.id);

    return (
      <div className="space-y-4">
        {/* 공간 정보 + 좌석 현황 */}
        <div className={`p-4 ${themeClasses.lightBg} rounded-lg border ${themeClasses.border}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">{space.name}</span>
            <Badge className={themeClasses.badge}>
              {stats.available}석 이용가능
            </Badge>
          </div>

          {/* 점유율 바 */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>사용중 {stats.occupied}석</span>
              <span>전체 {stats.total}석</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${themeClasses.progress} transition-all`}
                style={{ width: `${occupancyRate}%` }}
              />
            </div>
          </div>

          {/* 배치도 보기 버튼 */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLayoutModal({ isOpen: true, space })}
            className="w-full flex items-center justify-center gap-2"
          >
            <MapPin className="w-4 h-4" />
            배치도 보기
          </Button>
        </div>

        {/* 내 예약 상태 */}
        {myReservation && (
          <div className={`p-4 rounded-lg border-2 ${
            myReservation.status === 'pending'
              ? 'border-yellow-400 bg-yellow-50'
              : 'border-blue-400 bg-blue-50'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {myReservation.status === 'pending' ? (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <Check className="w-5 h-5 text-blue-600" />
                  )}
                  <span className="font-medium">
                    {myReservation.status === 'pending' ? '예약 대기중' : '착석 완료'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {myReservation.seat_number}번 자리 · {myReservation.time}
                </p>
                {myReservation.status === 'pending' && (
                  <p className="text-xs text-yellow-700 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    30분 내 착석 확인 필요
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {myReservation.status === 'pending' && (
                  <Button
                    size="sm"
                    onClick={() => onConfirmSeat(myReservation.id)}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    착석 확인
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onCancelReservation(myReservation.id)}
                  className="text-red-500 border-red-300 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 자리 선택 UI (예약이 없을 때만) */}
        {!myReservation && (
          <>
            {/* 선택된 자리 표시 */}
            {selectedSeat && selectedSeat.spaceId === space.id && (
              <div className={`p-4 rounded-lg border-2 ${themeClasses.border} ${themeClasses.lightBg}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{selectedSeat.seat.number}번 자리 선택됨</span>
                    <p className="text-sm text-gray-500">예약하시겠습니까?</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleReserve}
                      className={themeClasses.button}
                    >
                      예약하기
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedSeat(null)}
                    >
                      취소
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* 빈자리 빠른 선택 */}
            {!selectedSeat && stats.available > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">빈 자리 선택</h4>
                <div className="flex flex-wrap gap-2">
                  {spaceSeats
                    .filter(s => s.status === 'available')
                    .slice(0, 8)
                    .map(seat => (
                      <button
                        key={seat.id}
                        onClick={() => setSelectedSeat({ spaceId: space.id, seat })}
                        className={`w-10 h-10 rounded-lg border-2 font-medium text-sm
                          ${themeClasses.border} hover:${themeClasses.lightBg}
                          transition-all hover:scale-105`}
                      >
                        {seat.number}
                      </button>
                    ))}
                  {stats.available > 8 && (
                    <button
                      onClick={() => setLayoutModal({ isOpen: true, space })}
                      className="w-10 h-10 rounded-lg border-2 border-gray-300
                        text-gray-500 text-xs hover:bg-gray-50"
                    >
                      +{stats.available - 8}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 빈자리 없음 */}
            {stats.available === 0 && (
              <div className="text-center py-6 text-gray-500">
                <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>현재 이용 가능한 자리가 없습니다.</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}
